/**
 * Revisión semanal: hechos calculados con las mismas reglas del motor (omitidos y
 * vacaciones son neutrales; un periodo en curso no cuenta como incumplido). Solo
 * cifras y lo que el usuario eligió incluir; ninguna interpretación.
 */
import { addDays, logicalDateFor, weekPeriods, type EngineSettings, type LocalDate } from '@habitos/engine';
import type { FocusRec, HabitRec, JournalRec, VacationRec } from './model';
import { evaluate, liveHabits, type HabitIndex } from './selectors';

export interface ReviewInput {
  habits: HabitRec[];
  idx: HabitIndex;
  vacations: VacationRec[];
  focus: FocusRec[];
  journal: JournalRec[];
  settings: EngineSettings;
  tz: string;
  dayStartMinutes: number;
  today: LocalDate;
  /** 0 = semana actual, -1 = la anterior… */
  weekOffset: number;
}

export interface HabitWeek {
  habitId: string;
  name: string;
  completed: number;
  /** Periodos que contaban: completados, incumplidos y fallados. */
  eligible: number;
}

export interface WeekReview {
  start: LocalDate;
  end: LocalDate;
  /** La semana aún no ha terminado (se cuenta hasta hoy). */
  inProgress: boolean;
  habits: HabitWeek[];
  completed: number;
  eligible: number;
  /** completed / eligible, o null si no había nada que contara. */
  consistency: number | null;
  previousConsistency: number | null;
  tasksCompleted: number;
  focusMinutes: number;
  focusSessions: number;
  reflections: JournalRec[];
}

function weekOf(settings: EngineSettings, day: LocalDate) {
  return weekPeriods(settings, day, day)[0]!;
}

function habitsInWeek(input: ReviewInput, start: LocalDate, end: LocalDate): HabitWeek[] {
  const to = end < input.today ? end : input.today;
  if (to < start) return [];
  const out: HabitWeek[] = [];
  for (const h of liveHabits(input.habits)) {
    if (h.kind === 'task') continue;
    const results = evaluate(h, input.idx, input.vacations, input.settings, input.today, { from: start, to });
    // Una UE cuenta para la semana en la que termina (las mensuales, en la que acaba el mes).
    const inside = results.filter((r) => r.end >= start && r.end <= end);
    const completed = inside.filter((r) => r.status === 'completed').length;
    const eligible = inside.filter((r) => r.status === 'completed' || r.status === 'missed' || r.status === 'failed').length;
    if (eligible > 0 || completed > 0) out.push({ habitId: h.id, name: h.name, completed, eligible });
  }
  return out;
}

const ratio = (hs: HabitWeek[]) => {
  const e = hs.reduce((n, h) => n + h.eligible, 0);
  return e === 0 ? null : hs.reduce((n, h) => n + h.completed, 0) / e;
};

export function weekReview(input: ReviewInput): WeekReview {
  const anchor = addDays(input.today, 7 * input.weekOffset);
  const { start, end } = weekOf(input.settings, anchor);
  const prev = weekOf(input.settings, addDays(start, -1));
  const habits = habitsInWeek(input, start, end);
  const inWeek = (d: LocalDate) => d >= start && d <= end;

  const tasksCompleted = liveHabits(input.habits).filter(
    (h) => h.kind === 'task' && h.completedAt && inWeek(logicalDateFor(Date.parse(h.completedAt), input.tz, input.dayStartMinutes)),
  ).length;
  const focus = input.focus.filter((f) => !f.deletedAt && inWeek(f.logicalDate));

  return {
    start,
    end,
    inProgress: end >= input.today && start <= input.today,
    habits,
    completed: habits.reduce((n, h) => n + h.completed, 0),
    eligible: habits.reduce((n, h) => n + h.eligible, 0),
    consistency: ratio(habits),
    previousConsistency: ratio(habitsInWeek(input, prev.start, prev.end)),
    tasksCompleted,
    focusMinutes: Math.round(focus.reduce((n, f) => n + f.activeSeconds, 0) / 60),
    focusSessions: focus.length,
    reflections: input.journal
      .filter((j) => !j.deletedAt && j.includeInReview && inWeek(j.date))
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
  };
}
